/**
 * Place Rating Badge Component
 * 
 * Displays a compact rating badge with stars and count
 */

import React from 'react';
import { View, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { Caption } from '../ui/Text';

type PlaceRatingBadgeProps = {
  rating: number;
  totalRatings?: number;
  size?: 'small' | 'medium' | 'large';
  showCount?: boolean;
};

export const PlaceRatingBadge: React.FC<PlaceRatingBadgeProps> = ({
  rating,
  totalRatings,
  size = 'medium',
  showCount = true,
}) => {
  const theme = useTheme();

  const iconSize = size === 'small' ? 12 : size === 'large' ? 18 : 14;
  const fontSize = size === 'small' ? 11 : size === 'large' ? 14 : 12;

  // Round to nearest 0.5 for display
  const displayRating = Math.round(rating * 2) / 2;
  const fullStars = Math.floor(displayRating);
  const hasHalfStar = displayRating % 1 !== 0;

  return (
    <View style={styles.container}>
      <View style={styles.starsContainer}>
        {[...Array(5)].map((_, index) => {
          if (index < fullStars) {
            return (
              <Ionicons
                key={index}
                name="star"
                size={iconSize}
                color={theme.colors.primary}
              />
            );
          } else if (index === fullStars && hasHalfStar) {
            return (
              <Ionicons
                key={index}
                name="star-half"
                size={iconSize}
                color={theme.colors.primary}
              />
            );
          } else {
            return (
              <Ionicons
                key={index}
                name="star-outline"
                size={iconSize}
                color={theme.colors.textSecondary}
              />
            );
          }
        })}
      </View>
      <View style={styles.ratingInfo}>
        <Caption style={[styles.ratingText, { fontSize, color: theme.colors.text }]}>
          {displayRating.toFixed(1)}
        </Caption>
        {showCount && totalRatings !== undefined && totalRatings > 0 && (
          <Caption color={theme.colors.textSecondary} style={[styles.countText, { fontSize: fontSize - 1 }]}>
            ({totalRatings})
          </Caption>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  starsContainer: {
    flexDirection: 'row',
    gap: 2,
  },
  ratingInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginLeft: 4,
  },
  ratingText: {
    fontWeight: '600',
  },
  countText: {
    fontWeight: '400',
  },
});

